import React, { useEffect, useMemo, useRef } from 'react';
import {
  AccessibilityInfo,
  BackHandler,
  Platform,
  StatusBar,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  findNodeHandle,
} from 'react-native';
import Ionicons from '@react-native-vector-icons/ionicons';
import { SystemBars } from 'react-native-edge-to-edge';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import KeyboardAwareFormScroll from '../../../components/Reusables/KeyboardAwareFormScroll';
import { GTFonts, useLayoutSize, useTheme, type Theme } from '../../../theme';
import { WEB_MODAL_MAX_WIDTH } from '../../../theme/webLayoutWidths';
import { borderWidth, fs, iconSize, s, v } from '../../../utils/libs';

/**
 * Shared responsive wizard shell (US2, T041). Owns the header (Back,
 * text-semantic progress, post-Step-1 Skip), scrollable content, and the
 * sticky action area. Hardware/gesture Back is intercepted: Step 1 never
 * returns to Home or logged-out auth history.
 */

export interface SetupWizardShellProps {
  /** 0-based index of the visible step. */
  stepIndex: number;
  totalSteps: number;
  /** Small eyebrow/context line above the heading. */
  eyebrow?: string;
  heading: string;
  supportingCopy?: string;
  /** Back is hidden on Step 1. */
  onBack?: () => void;
  /** Skip setup is only rendered after Step 1. */
  onSkip?: () => void;
  primaryLabel: string;
  onPrimaryPress: () => void;
  primaryDisabled?: boolean;
  /** Busy state announced on the primary action (e.g. product submitting). */
  primaryBusy?: boolean;
  secondaryLabel?: string;
  onSecondaryPress?: () => void;
  secondaryAccessibilityHint?: string;
  children?: React.ReactNode;
  testID?: string;
}

const SetupWizardShell: React.FC<SetupWizardShellProps> = ({
  stepIndex,
  totalSteps,
  eyebrow,
  heading,
  supportingCopy,
  onBack,
  onSkip,
  primaryLabel,
  onPrimaryPress,
  primaryDisabled = false,
  primaryBusy = false,
  secondaryLabel,
  onSecondaryPress,
  secondaryAccessibilityHint,
  children,
  testID,
}) => {
  const { theme } = useTheme();
  const { useWebLayout, sizeClass } = useLayoutSize();
  const insets = useSafeAreaInsets();
  // Tablets and web/Electron read better with the modal-width column; phones
  // keep the full width (T047).
  const constrainWidth = useWebLayout || sizeClass !== 'compact';
  const styles = useMemo(
    () => createStyles(theme, constrainWidth),
    [theme, constrainWidth],
  );
  const headingRef = useRef<Text>(null);

  // Keyboard focus / screen-reader announcement moves to the new step
  // heading whenever the step changes (UI/UX contract §7).
  useEffect(() => {
    const timer = setTimeout(() => {
      const node = headingRef.current;
      if (!node) return;
      if (Platform.OS === 'web') {
        (node as any)?.focus?.();
        return;
      }
      const handle = findNodeHandle(node);
      if (handle) {
        AccessibilityInfo.setAccessibilityFocus(handle);
      }
    }, 120);
    return () => clearTimeout(timer);
  }, [stepIndex]);

  // Android hardware Back follows wizard Back rules; on Step 1 it does
  // nothing rather than exposing Home or the logged-out auth history.
  useEffect(() => {
    const subscription = BackHandler.addEventListener(
      'hardwareBackPress',
      () => {
        if (onBack) {
          onBack();
        }
        return true;
      },
    );
    return () => subscription.remove();
  }, [onBack]);

  const progressText = `Step ${stepIndex + 1} of ${totalSteps}`;

  return (
    <View style={styles.page} testID={testID}>
      {/* Match SafeAreaWrapper: edge-to-edge status bar so the inset band
          paints with the page background instead of a solid system strip. */}
      {Platform.OS === 'android' ? (
        <SystemBars style="dark" />
      ) : (
        <StatusBar
          barStyle="dark-content"
          backgroundColor="transparent"
          translucent
          animated
        />
      )}
      <View style={styles.contentColumn}>
        {/* The wizard route renders with headerShown: false, so the shell
            owns the top/bottom safe areas itself. */}
        <View style={[styles.header, { marginTop: insets.top }]}>
          <View style={styles.headerSide}>
            {onBack ? (
              <TouchableOpacity
                onPress={onBack}
                accessibilityRole="button"
                accessibilityLabel="Back"
                accessibilityHint="Return to the previous step"
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                style={styles.backButton}
                testID="wizard-back">
                <Ionicons
                  name="chevron-back"
                  size={iconSize(22)}
                  color={theme.colors.text}
                />
              </TouchableOpacity>
            ) : null}
          </View>
          <Text
            style={styles.progressText}
            accessibilityRole="text"
            accessibilityLiveRegion="polite"
            testID="wizard-progress">
            {progressText}
          </Text>
          <View style={[styles.headerSide, styles.headerSideRight]}>
            {onSkip ? (
              <TouchableOpacity
                onPress={onSkip}
                accessibilityRole="button"
                accessibilityLabel="Skip setup"
                accessibilityHint="Leave this guide and go to Home"
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                testID="wizard-skip">
                <Text style={styles.skipText}>Skip setup</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        </View>

        <KeyboardAwareFormScroll
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
          bottomOffset={v(160)}>
          {eyebrow ? <Text style={styles.eyebrow}>{eyebrow}</Text> : null}
          <Text
            ref={headingRef}
            style={styles.heading}
            accessibilityRole="header"
            // @ts-ignore web-only: focusable heading for keyboard focus move
            focusable={Platform.OS === 'web'}
            testID="wizard-heading">
            {heading}
          </Text>
          {supportingCopy ? (
            <Text style={styles.supportingCopy}>{supportingCopy}</Text>
          ) : null}
          <View style={styles.stepContent}>{children}</View>
        </KeyboardAwareFormScroll>

        <View
          style={[
            styles.actionArea,
            { paddingBottom: v(16) + insets.bottom },
          ]}>
          <TouchableOpacity
            style={[
              styles.primaryButton,
              (primaryDisabled || primaryBusy) && styles.primaryButtonDisabled,
            ]}
            onPress={onPrimaryPress}
            disabled={primaryDisabled || primaryBusy}
            accessibilityRole="button"
            accessibilityLabel={primaryLabel}
            accessibilityState={{
              disabled: primaryDisabled || primaryBusy,
              busy: primaryBusy,
            }}
            testID="wizard-primary">
            <Text style={styles.primaryButtonText}>{primaryLabel}</Text>
          </TouchableOpacity>
          {secondaryLabel && onSecondaryPress ? (
            <TouchableOpacity
              style={styles.secondaryButton}
              onPress={onSecondaryPress}
              accessibilityRole="button"
              accessibilityLabel={secondaryLabel}
              accessibilityHint={secondaryAccessibilityHint}
              testID="wizard-secondary">
              <Text style={styles.secondaryButtonText}>{secondaryLabel}</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
    </View>
  );
};

const createStyles = (theme: Theme, constrainWidth: boolean) =>
  StyleSheet.create({
    page: {
      flex: 1,
      // White like auth/setup flows — theme.colors.background reads as a
      // pale empty field when most of the step is text + bordered cards.
      backgroundColor: theme.colors.surface,
      alignItems: 'center',
    },
    contentColumn: {
      flex: 1,
      width: '100%',
      ...(constrainWidth && {
        maxWidth: WEB_MODAL_MAX_WIDTH,
        alignSelf: 'center' as const,
      }),
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: s(16),
      paddingVertical: v(12),
      minHeight: v(48),
    },
    headerSide: {
      minWidth: s(72),
      flexDirection: 'row',
    },
    headerSideRight: {
      justifyContent: 'flex-end',
    },
    backButton: {
      padding: s(4),
      marginLeft: -s(4),
    },
    progressText: {
      flex: 1,
      textAlign: 'center',
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.textSecondary,
    },
    skipText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.primary,
    },
    scroll: {
      flex: 1,
    },
    scrollContent: {
      paddingHorizontal: s(20),
      paddingTop: v(8),
      paddingBottom: v(32),
    },
    eyebrow: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(12),
      lineHeight: fs(16),
      letterSpacing: 1.2,
      textTransform: 'uppercase',
      color: theme.colors.success,
      marginBottom: v(8),
    },
    heading: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(24),
      lineHeight: fs(30),
      color: theme.colors.text,
      marginBottom: v(8),
      ...(Platform.OS === 'web' && ({ outlineStyle: 'none' } as any)),
    },
    supportingCopy: {
      fontFamily: GTFonts.regular,
      fontSize: fs(16),
      lineHeight: fs(22),
      color: theme.colors.text,
      marginVertical: v(16),
    },
    stepContent: {
      flexGrow: 1,
    },
    actionArea: {
      paddingHorizontal: s(20),
      paddingTop: v(10),
      backgroundColor: theme.colors.surface,
      borderTopWidth: borderWidth(1),
      borderTopColor: theme.colors.borderSecondary,
      gap: v(8),
    },
    primaryButton: {
      backgroundColor: theme.colors.success,
      borderRadius: theme.borderRadius.md,
      paddingVertical: v(14),
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: v(48),
    },
    primaryButtonDisabled: {
      opacity: 0.55,
    },
    primaryButtonText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(20),
      color: theme.colors.surface,
    },
    secondaryButton: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: v(10),
      minHeight: v(40),
    },
    secondaryButtonText: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.primary,
    },
  });

export default SetupWizardShell;
